import * as React from "react"
import { useForm, useWatch } from "react-hook-form"
import { format } from "date-fns"
import { CalendarIcon, Plus, X, AlertCircle } from "lucide-react"

import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import type { DateRange } from "react-day-picker"

import { Tabs, TabsList, TabsTrigger } from "@/shared/ui/atoms/Tabs"
import { Input } from "@/shared/ui/atoms/Input"
import { Button } from "@/shared/ui/atoms/Button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/atoms/Select"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/atoms/Popover"
import { Calendar } from "@/shared/ui/atoms/Calendar"
import { Switch } from "@/shared/ui/atoms/Switch"
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/shared/ui/atoms/Form"
import { cn } from "@/lib/utils"
import type { Account, Transaction } from "@/types"
import { useCreateTransaction, useUpdateTransaction } from "../../hooks/useTransactions"
import { useCategories } from "../../hooks/useCategories"
import { useCreateCategory } from "../../hooks/useCreateCategory"

interface TransactionFormProps {
  accounts: Account[]
  initialTab?: "EXPENSE" | "INCOME" | "SAVING" | "TRANSFER"
  initialTransaction?: (Transaction & { _type?: "EXPENSE" | "INCOME" | "SAVING" | "TRANSFER" }) | null
  onSuccess?: () => void
  onCancel?: () => void
}

const formSchema = z
  .object({
    actionType: z.enum(["EXPENSE", "INCOME", "SAVING", "TRANSFER"]),
    amount: z.string().min(1, "El importe es obligatorio"),
    fee: z.string().optional(),
    description: z.string().min(1, "El concepto es obligatorio"),
    account: z.string().optional(),
    from_account: z.string().optional(),
    to_account: z.string().optional(),
    category: z.string().optional(),
    isRange: z.boolean(),
    date: z.date({ error: "La fecha es obligatoria" }),
    endDate: z.date().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.actionType === "TRANSFER") {
      if (!data.from_account) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La cuenta origen es obligatoria",
          path: ["from_account"],
        })
      }
      if (!data.to_account) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La cuenta destino es obligatoria",
          path: ["to_account"],
        })
      }
      if (data.from_account && data.to_account && data.from_account === data.to_account) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La cuenta origen y destino no pueden ser la misma",
          path: ["to_account"],
        })
      }
    } else {
      if (!data.account) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La cuenta es obligatoria",
          path: ["account"],
        })
      }
    }
  })

type FormValues = z.infer<typeof formSchema>

export function TransactionForm({
  accounts,
  initialTab = "EXPENSE",
  initialTransaction,
  onSuccess,
  onCancel,
}: TransactionFormProps) {
  const isEditing = Boolean(initialTransaction)
  const { data: categories = [] } = useCategories()
  const { mutate: createTransaction, isPending: isCreating } = useCreateTransaction()
  const { mutate: updateTransaction, isPending: isUpdating } = useUpdateTransaction()
  const { mutateAsync: createCategory, isPending: isCreatingCategory } = useCreateCategory()
  const isPending = isCreating || isUpdating

  const [isAddingCategory, setIsAddingCategory] = React.useState(false)
  const [newCategoryName, setNewCategoryName] = React.useState("")
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([])

  // Parse initial dates if editing
  const initialStartDate = initialTransaction?.date
    ? new Date(initialTransaction.date.includes("T") ? initialTransaction.date : `${initialTransaction.date}T12:00:00`)
    : new Date()

  const initialEndDate = initialTransaction?.end_date
    ? new Date(initialTransaction.end_date.includes("T") ? initialTransaction.end_date : `${initialTransaction.end_date}T12:00:00`)
    : null

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      actionType: initialTransaction?._type || initialTab,
      amount: initialTransaction?.amount ? String(initialTransaction.amount) : "",
      fee: initialTransaction?.fee ? String(initialTransaction.fee) : "0.00",
      description: initialTransaction?.description || "",
      account: initialTransaction?.account ? String(initialTransaction.account) : "",
      from_account: initialTransaction?.from_account
        ? String(initialTransaction.from_account)
        : (initialTransaction?.account ? String(initialTransaction.account) : ""),
      to_account: initialTransaction?.to_account ? String(initialTransaction.to_account) : "",
      category: initialTransaction?.category ? String(initialTransaction.category) : "",
      isRange: Boolean(initialEndDate),
      date: initialStartDate,
      endDate: initialEndDate,
    },
  })

  const actionType = useWatch({ control: form.control, name: "actionType" })
  const isRange = useWatch({ control: form.control, name: "isRange" })
  const startDate = useWatch({ control: form.control, name: "date" })
  const endDate = useWatch({ control: form.control, name: "endDate" })
  const fromAccountId = useWatch({ control: form.control, name: "from_account" })
  const toAccountId = useWatch({ control: form.control, name: "to_account" })

  const fromAccountObj = accounts.find((a) => a.id.toString() === fromAccountId)
  const toAccountObj = accounts.find((a) => a.id.toString() === toAccountId)
  const hasCurrencyMismatch =
    fromAccountObj &&
    toAccountObj &&
    fromAccountObj.currency !== toAccountObj.currency

  const selectableAccounts =
    actionType === "SAVING"
      ? accounts.filter((account) => account.is_savings_account)
      : accounts

  const handleSubmit = (values: FormValues) => {
    const payloadDate = format(values.date, "yyyy-MM-dd")
    const payloadEndDate =
      values.isRange && values.endDate ? format(values.endDate, "yyyy-MM-dd") : null

    if (values.actionType === "TRANSFER") {
      if (isEditing && initialTransaction) {
        updateTransaction(
          {
            id: initialTransaction.id,
            from_account: parseInt(values.from_account!, 10),
            to_account: parseInt(values.to_account!, 10),
            amount: values.amount,
            fee: values.fee || "0.00",
            description: values.description,
            date: payloadDate,
            actionType: "TRANSFER",
            uploaded_files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
          },
          {
            onSuccess: () => {
              onSuccess?.()
            },
          }
        )
      } else {
        createTransaction(
          {
            from_account: parseInt(values.from_account!, 10),
            to_account: parseInt(values.to_account!, 10),
            amount: values.amount,
            fee: values.fee || "0.00",
            description: values.description,
            date: payloadDate,
            actionType: "TRANSFER",
            uploaded_files: uploadedFiles,
          },
          {
            onSuccess: () => {
              form.reset()
              setUploadedFiles([])
              form.setValue("actionType", actionType)
              onSuccess?.()
            },
          }
        )
      }
      return
    }

    const categoryId =
      values.actionType === "SAVING" || !values.category
        ? undefined
        : parseInt(values.category, 10)

    if (isEditing && initialTransaction) {
      updateTransaction(
        {
          id: initialTransaction.id,
          account: parseInt(values.account!, 10),
          category: categoryId,
          amount: values.amount,
          description: values.description,
          date: payloadDate,
          end_date: payloadEndDate,
          actionType: values.actionType,
          uploaded_files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        },
        {
          onSuccess: () => {
            onSuccess?.()
          },
        }
      )
    } else {
      createTransaction(
        {
          account: parseInt(values.account!, 10),
          category: categoryId,
          amount: values.amount,
          description: values.description,
          date: payloadDate,
          end_date: payloadEndDate,
          actionType: values.actionType,
          uploaded_files: uploadedFiles,
        },
        {
          onSuccess: () => {
            form.reset()
            setUploadedFiles([])
            form.setValue("actionType", actionType)
            onSuccess?.()
          },
        }
      )
    }
  }

  const handleCreateCategory = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (!newCategoryName.trim()) return
    try {
      const newCat = await createCategory(newCategoryName.trim())
      form.setValue("category", newCat.id.toString())
      setIsAddingCategory(false)
      setNewCategoryName("")
    } catch {
      // Error handled by hook toast
    }
  }

  const dateRangeValue: DateRange | undefined = isRange
    ? { from: startDate, to: endDate || undefined }
    : undefined

  const handleRangeSelect = (range: DateRange | undefined) => {
    if (range?.from) {
      form.setValue("date", range.from)
      form.setValue("endDate", range.to || null)
    }
  }

  return (
    <Tabs
      value={actionType}
      onValueChange={(v) => form.setValue("actionType", v as FormValues["actionType"])}
      className="w-full"
    >
      {!isEditing && (
        <div className="pb-5">
          <TabsList className="grid w-full grid-cols-4 bg-muted/50 rounded-full p-1 border">
            <TabsTrigger
              value="EXPENSE"
              className="rounded-full data-[state=active]:bg-accent-danger data-[state=active]:text-white transition-all text-xs sm:text-sm"
            >
              Gasto
            </TabsTrigger>
            <TabsTrigger
              value="INCOME"
              className="rounded-full data-[state=active]:bg-foreground data-[state=active]:text-background transition-all text-xs sm:text-sm"
            >
              Ingreso
            </TabsTrigger>
            <TabsTrigger
              value="TRANSFER"
              className="rounded-full data-[state=active]:bg-sky-600 data-[state=active]:text-white transition-all text-xs sm:text-sm"
            >
              Transferencia
            </TabsTrigger>
            <TabsTrigger
              value="SAVING"
              className="rounded-full data-[state=active]:bg-accent-warning data-[state=active]:text-background transition-all text-xs sm:text-sm"
            >
              Ahorro
            </TabsTrigger>
          </TabsList>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {actionType === "TRANSFER" ? "Importe a Transferir" : "Importe"}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="text-lg font-mono"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Concepto / Descripción</FormLabel>
                <FormControl>
                  <Input
                    placeholder={
                      actionType === "TRANSFER"
                        ? "Ej. Traspaso a cuenta de ahorro, Retiro efectivo..."
                        : "Ej. Supermercado, Salario, Ahorro mensual..."
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {actionType === "TRANSFER" ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                <FormField
                  control={form.control}
                  name="from_account"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cuenta Origen</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar origen" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {accounts.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id.toString()}>
                              {acc.name} ({acc.currency})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-[11px]">
                        De aquí saldrán los fondos y la comisión.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="to_account"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cuenta Destino</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar destino" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {accounts.map((acc) => (
                            <SelectItem key={acc.id} value={acc.id.toString()}>
                              {acc.name} ({acc.currency})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription className="text-[11px]">
                        Recibirá el importe transferido.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {hasCurrencyMismatch && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-accent-warning/30 bg-accent-warning/10 text-xs text-accent-warning">
                  <AlertCircle className="size-4 shrink-0" />
                  <span>
                    Las cuentas tienen diferentes divisas ({fromAccountObj?.currency} y {toAccountObj?.currency}). Las transferencias deben ser en la misma moneda.
                  </span>
                </div>
              )}

              <FormField
                control={form.control}
                name="fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Comisión / Costo por Transferencia (Opcional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className="font-mono text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-[11px]">
                      Se restará de la cuenta origen junto al importe (editable en cualquier momento).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ) : (
            <div className={cn("grid gap-4", actionType === "SAVING" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2")}>
              <FormField
                control={form.control}
                name="account"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cuenta</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar cuenta" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {selectableAccounts.map((acc) => (
                          <SelectItem key={acc.id} value={acc.id.toString()}>
                            {acc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {actionType !== "SAVING" ? (
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Categoría</FormLabel>
                        {!isAddingCategory && (
                          <button
                            type="button"
                            onClick={() => setIsAddingCategory(true)}
                            className="text-[10px] uppercase font-bold text-primary hover:underline flex items-center gap-1"
                          >
                            <Plus className="size-3" /> Nueva
                          </button>
                        )}
                      </div>
                      {isAddingCategory ? (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Nombre de categoría..."
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            className="h-9"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            className="h-9 px-3 shrink-0"
                            onClick={handleCreateCategory}
                            disabled={isCreatingCategory || !newCategoryName.trim()}
                          >
                            Crear
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 shrink-0 text-muted-foreground"
                            onClick={() => setIsAddingCategory(false)}
                          >
                            <X className="size-4" />
                          </Button>
                        </div>
                      ) : (
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar categoría" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id.toString()}>
                                {cat.name}
                              </SelectItem>
                            ))}
                            {categories.length === 0 && (
                              <div className="p-2 text-sm text-muted-foreground text-center">
                                No hay categorías creadas
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <div className="flex items-center rounded-md bg-muted/50 px-3 text-sm text-muted-foreground">
                  Los ahorros no requieren categoría.
                </div>
              )}
            </div>
          )}

          {/* Date & Date Range Selector */}
          <div className="space-y-3 pt-1">
            {actionType === "EXPENSE" && (
              <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/70 bg-muted/20">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-foreground">¿Gasto con rango de fechas?</span>
                  <span className="text-[11px] text-muted-foreground">
                    Para gastos que cubren un periodo (ej. suscripciones, servicios, viajes).
                  </span>
                </div>
                <Switch
                  checked={isRange}
                  onCheckedChange={(checked) => {
                    form.setValue("isRange", checked)
                    if (!checked) {
                      form.setValue("endDate", null)
                    }
                  }}
                />
              </div>
            )}

            {isRange && actionType === "EXPENSE" ? (
              <div className="space-y-1.5">
                <FormLabel>Rango de Fechas</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                      {startDate ? (
                        endDate ? (
                          <span>
                            {format(startDate, "dd MMM, yyyy")} → {format(endDate, "dd MMM, yyyy")}
                          </span>
                        ) : (
                          <span>Desde {format(startDate, "dd MMM, yyyy")} (Selecciona fin)</span>
                        )
                      ) : (
                        <span>Selecciona rango</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      defaultMonth={startDate}
                      selected={dateRangeValue}
                      onSelect={handleRangeSelect}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription className="text-[11px]">
                  El gasto abarcará todo el periodo comprendido entre las dos fechas.
                </FormDescription>
              </div>
            ) : (
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Fecha</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                            {field.value ? format(field.value, "PPP") : <span>Seleccionar fecha</span>}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Attachments */}
          <div className="space-y-2 pt-1">
            <FormLabel className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">
              Comprobantes / Adjuntos (Opcional)
            </FormLabel>
            <div className="border border-dashed rounded-lg p-3 bg-muted/20 hover:bg-muted/40 transition-colors">
              <Input
                type="file"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    setUploadedFiles(Array.from(e.target.files))
                  }
                }}
                className="file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-medium file:bg-primary/10 file:text-primary hover:file:cursor-pointer hover:file:bg-primary/20 text-xs border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
              />
            </div>
            {uploadedFiles.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {uploadedFiles.map((file, i) => (
                  <div key={i} className="text-xs bg-secondary px-2 py-1 rounded-md flex items-center gap-1">
                    <span className="truncate max-w-[150px]">{file.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 pt-4">
            {onCancel ? (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
                className="w-full sm:w-auto"
              >
                Cancelar
              </Button>
            ) : null}
            <Button type="submit" className="w-full sm:w-auto min-w-[140px]" disabled={isPending}>
              {isPending
                ? "Guardando…"
                : isEditing
                ? "Actualizar registro"
                : "Guardar registro"}
            </Button>
          </div>
        </form>
      </Form>
    </Tabs>
  )
}

export default TransactionForm

